import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

/**
 * CampaignCustomizerModal — preview + per-candidate step customization.
 *
 * Shown after the user picks a campaign on the "Add to Campaign" flow.
 * Lets the recruiter tweak subject/body for each step before enrolling.
 * Edits are returned to the caller as a `step_overrides` map and persisted
 * on the resulting enrollment row — the campaign template stays untouched.
 *
 * Mirrors the Chrome extension's customizer panel: same data flow
 * (campaign-with-steps + merge-fields + preview-email endpoint) and the
 * same per-step override JSON shape that the executor honors before
 * falling back to step.config.
 */
const CampaignCustomizerModal = ({ campaignId, campaignName, candidateId, onCancel, onContinue, inline = false, onBack = null }) => {
  const [loading, setLoading] = useState(true);
  const [mergeFields, setMergeFields] = useState([]);
  const [steps, setSteps] = useState([]);
  // previewMap shape: { [stepId]: { subject, body } | 'loading' | null }
  const [previewMap, setPreviewMap] = useState({});

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [campaignId]);

  const load = async () => {
    setLoading(true);
    try {
      // Parallel fetches: campaign-with-steps + the merge-fields list
      // recruiter-facing. The fields list is cached on the backend, so a
      // request per modal-open is fine.
      const [campRes, fieldsRes] = await Promise.all([
        fetch(`${API}/api/outreach/campaigns/${campaignId}`, { headers }),
        fetch(`${API}/api/outreach/merge-fields`, { headers })
      ]);
      const camp = campRes.ok ? await campRes.json() : { steps: [] };
      const fields = fieldsRes.ok ? await fieldsRes.json() : [];
      setMergeFields(Array.isArray(fields) ? fields : []);
      setSteps((camp.steps || []).map(s => ({
        id: s.id,
        position: s.position,
        step_type: s.step_type,
        config: s.config || {},
        // Working copies — edited in place; reset button restores from config.
        customSubject: (s.config && s.config.subject) || '',
        customBody: (s.config && s.config.body) || '',
        // Manual-task working copies (title/description for generic; subject/body
        // reuse the fields above for InMail).
        customTitle: (s.config && s.config.title) || '',
        customDescription: (s.config && s.config.description) || ''
      })));
    } finally {
      setLoading(false);
    }
  };

  const updateStep = (idx, patch) => {
    setSteps(prev => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    // Editing the step invalidates any open preview — clear it so the user
    // doesn't see stale resolved content.
    const stepId = steps[idx]?.id;
    if (stepId != null) {
      setPreviewMap(prev => {
        if (!(stepId in prev)) return prev;
        const next = { ...prev };
        delete next[stepId];
        return next;
      });
    }
  };

  const resetStep = (idx) => {
    setSteps(prev => prev.map((s, i) => (
      i === idx
        ? { ...s, customSubject: (s.config && s.config.subject) || '', customBody: (s.config && s.config.body) || '' }
        : s
    )));
    const stepId = steps[idx]?.id;
    if (stepId != null) setPreviewMap(prev => { const n = { ...prev }; delete n[stepId]; return n; });
  };

  const insertMergeField = (idx, field, key) => {
    const step = steps[idx];
    const current = step[field] || '';
    updateStep(idx, { [field]: current + `{{${key}}}` });
  };

  const togglePreview = async (idx) => {
    const step = steps[idx];
    const existing = previewMap[step.id];
    if (existing) {
      // Close
      setPreviewMap(prev => { const n = { ...prev }; delete n[step.id]; return n; });
      return;
    }
    setPreviewMap(prev => ({ ...prev, [step.id]: 'loading' }));
    try {
      const res = await fetch(`${API}/api/outreach/preview-email`, {
        method: 'POST', headers,
        body: JSON.stringify({
          subject: step.customSubject,
          body: step.customBody,
          candidate_id: candidateId,
          pipeline_id: null
        })
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewMap(prev => ({ ...prev, [step.id]: { subject: data.subject || '', body: data.body || '' } }));
      } else {
        setPreviewMap(prev => ({ ...prev, [step.id]: { subject: '(preview failed)', body: '' } }));
      }
    } catch (e) {
      setPreviewMap(prev => ({ ...prev, [step.id]: { subject: '(preview failed)', body: '' } }));
    }
  };

  const handleContinue = () => {
    // Build a step_overrides map keyed by step position. Only include steps
    // whose content actually changed — saves payload size and makes "no
    // edits" a clean no-op on the backend.
    const overrides = {};
    for (const s of steps) {
      const defaultSubject = (s.config && s.config.subject) || '';
      const defaultBody = (s.config && s.config.body) || '';

      if (s.step_type === 'manual_task') {
        const taskType = (s.config && s.config.task_type) || 'generic';
        if (taskType === 'inmail') {
          const subjChanged = s.customSubject !== defaultSubject;
          const bodyChanged = s.customBody !== defaultBody;
          if (subjChanged || bodyChanged) {
            overrides[s.position] = { task_type: 'inmail', subject: s.customSubject, body: s.customBody };
          }
        } else {
          const defaultTitle = (s.config && s.config.title) || '';
          const defaultDesc = (s.config && s.config.description) || '';
          const titleChanged = s.customTitle !== defaultTitle;
          const descChanged = s.customDescription !== defaultDesc;
          if (titleChanged || descChanged) {
            overrides[s.position] = { task_type: 'generic', title: s.customTitle, description: s.customDescription };
          }
        }
        continue;
      }

      const subjectChanged = s.customSubject !== defaultSubject;
      const bodyChanged = s.customBody !== defaultBody;
      if (subjectChanged || bodyChanged) {
        overrides[s.position] = {
          subject: s.customSubject,
          body: s.customBody
        };
      }
    }
    onContinue(overrides);
  };

  const dirtyCount = steps.reduce((acc, s) => {
    const defaultSubject = (s.config && s.config.subject) || '';
    const defaultBody = (s.config && s.config.body) || '';
    return acc + ((s.customSubject !== defaultSubject || s.customBody !== defaultBody) ? 1 : 0);
  }, 0);

  const renderStepCard = (s, idx) => {
    const stepType = s.step_type || 'manual';
    const preview = previewMap[s.id];
    const isPreviewOpen = !!preview;
    const isLoading = preview === 'loading';

    const badgeStyles = {
      email: 'bg-blue-100 text-blue-700',
      sms: 'bg-green-100 text-green-700',
      wait: 'bg-gray-100 text-gray-600',
      manual_task: 'bg-amber-100 text-amber-700',
      task: 'bg-amber-100 text-amber-700'
    };

    return (
      <div key={s.id} className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">Step {idx + 1}</span>
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${badgeStyles[stepType] || 'bg-gray-100 text-gray-600'}`}>
              {stepType.replace('_', ' ')}
            </span>
            <span className="text-xs text-gray-500">
              {stepType === 'wait'
                ? `Wait ${s.config?.days || 0} day(s)`
                : idx === 0 ? 'Send immediately' : 'After previous step'}
            </span>
          </div>
          {(stepType === 'email' || stepType === 'sms') && (
            <button
              onClick={() => resetStep(idx)}
              className="text-[10px] text-gray-400 hover:text-gray-700 underline"
              title="Restore the campaign template default for this step"
            >
              Reset to default
            </button>
          )}
        </div>

        {stepType === 'wait' && (
          <div className="text-xs text-gray-500 italic">Pause between sends — no content to customize.</div>
        )}
        {(stepType === 'manual_task' || stepType === 'task') && (() => {
          const taskType = (s.config && s.config.task_type) || 'generic';
          if (taskType === 'inmail') {
            return (
              <div className="space-y-2">
                <div className="text-[10px] text-blue-600 font-medium uppercase tracking-wide">LinkedIn InMail</div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
                  <div className="flex items-center gap-1 flex-wrap mb-1.5">
                    <span className="text-[10px] text-gray-400">Insert:</span>
                    {mergeFields.map(f => (
                      <button key={f.key} onClick={() => insertMergeField(idx, 'customSubject', f.key)}
                        className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded border border-blue-200 hover:bg-blue-100">{f.label || f.key}</button>
                    ))}
                  </div>
                  <input type="text" value={s.customSubject}
                    onChange={(e) => updateStep(idx, { customSubject: e.target.value })}
                    placeholder="InMail subject" className="w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
                  <div className="flex items-center gap-1 flex-wrap mb-1.5">
                    <span className="text-[10px] text-gray-400">Insert:</span>
                    {mergeFields.map(f => (
                      <button key={f.key} onClick={() => insertMergeField(idx, 'customBody', f.key)}
                        className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded border border-blue-200 hover:bg-blue-100">{f.label || f.key}</button>
                    ))}
                  </div>
                  <textarea value={s.customBody} onChange={(e) => updateStep(idx, { customBody: e.target.value })}
                    rows={6} placeholder="InMail message for this candidate…"
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 resize-none outline-none" />
                </div>
              </div>
            );
          }
          return (
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Task title</label>
                <input type="text" value={s.customTitle}
                  onChange={(e) => updateStep(idx, { customTitle: e.target.value })}
                  placeholder="Task title" className="w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <textarea value={s.customDescription} onChange={(e) => updateStep(idx, { customDescription: e.target.value })}
                  rows={3} placeholder="Notes / instructions for this candidate…"
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 resize-none outline-none" />
              </div>
            </div>
          );
        })()}

        {stepType === 'email' && (
          <>
            <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
            <div className="mb-2">
              <div className="flex items-center gap-1 flex-wrap mb-1.5">
                <span className="text-[10px] text-gray-400">Insert:</span>
                {mergeFields.map(f => (
                  <button
                    key={f.key}
                    onClick={() => insertMergeField(idx, 'customSubject', f.key)}
                    className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded border border-blue-200 hover:bg-blue-100"
                    title={f.example ? `e.g. ${f.example}` : ''}
                  >
                    {f.label || f.key}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={s.customSubject}
                onChange={(e) => updateStep(idx, { customSubject: e.target.value })}
                placeholder="Email subject"
                className="w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
          </>
        )}

        {(stepType === 'email' || stepType === 'sms') && (
          <>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-600">{stepType === 'email' ? 'Body' : 'Message'}</label>
              <button
                onClick={() => togglePreview(idx)}
                className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${isPreviewOpen ? 'bg-purple-100 text-purple-700 border-purple-300' : 'text-gray-500 border-gray-300 hover:bg-gray-50'}`}
              >
                {isPreviewOpen ? '✓ Preview' : 'Preview for this candidate'}
              </button>
            </div>
            <div className="flex items-center gap-1 flex-wrap mb-1.5">
              <span className="text-[10px] text-gray-400">Insert:</span>
              {mergeFields.map(f => (
                <button
                  key={f.key}
                  onClick={() => insertMergeField(idx, 'customBody', f.key)}
                  className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded border border-blue-200 hover:bg-blue-100"
                  title={f.example ? `e.g. ${f.example}` : ''}
                >
                  {f.label || f.key}
                </button>
              ))}
            </div>
            {isPreviewOpen ? (
              <div className="border rounded-lg overflow-hidden bg-gray-50">
                <div className="bg-white border-b px-3 py-1.5 text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                  Resolved for this candidate
                </div>
                <div className="p-3 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {isLoading ? <span className="text-gray-400 italic">Loading…</span> : (preview && preview.body) || <span className="text-gray-400 italic">(empty)</span>}
                </div>
              </div>
            ) : (
              <textarea
                value={s.customBody}
                onChange={(e) => updateStep(idx, { customBody: e.target.value })}
                placeholder={stepType === 'email' ? `Hi {{first_name}},\n\n...\n\nBest,\n{{sender_name}}` : `SMS message — keep it short.`}
                rows={stepType === 'sms' ? 3 : 7}
                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 resize-none font-mono outline-none"
              />
            )}
          </>
        )}
      </div>
    );
  };

  // Inline mode — used when this component renders inside a sidebar
  // panel instead of a floating modal. Content is identical; only the
  // outer chrome + spacing differ. `onBack` (if provided) becomes the
  // secondary button label; otherwise it falls back to onCancel.
  if (inline) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden bg-white">
        <div className="px-3 py-2 border-b flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <h3 className="text-xs font-semibold text-gray-900 truncate">Customize Campaign</h3>
            <div className="text-[10px] text-gray-500 truncate">
              {campaignName}
              {dirtyCount > 0 && (
                <span className="ml-1 text-purple-600 font-medium">· {dirtyCount} edited</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3 space-y-3 bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : steps.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-xs text-gray-500">This campaign has no steps yet.</p>
              <p className="text-[10px] text-gray-400 mt-1">Add steps to the campaign template before enrolling candidates.</p>
            </div>
          ) : (
            steps.map((s, idx) => renderStepCard(s, idx))
          )}
        </div>

        <div className="px-3 py-2 border-t bg-white flex-shrink-0">
          <div className="text-[10px] text-gray-500 mb-2">
            Edits apply to this enrollment only. The campaign template is not changed.
          </div>
          <div className="flex gap-2">
            <button
              onClick={onBack || onCancel}
              className="flex-1 px-3 py-1.5 text-xs border rounded hover:bg-gray-50"
            >
              {onBack ? 'Back' : 'Cancel'}
            </button>
            <button
              onClick={handleContinue}
              disabled={loading || steps.length === 0}
              className="flex-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Customize Campaign</h3>
            <div className="text-xs text-gray-500 mt-0.5">
              {campaignName}
              {dirtyCount > 0 && (
                <span className="ml-2 text-purple-600 font-medium">
                  · {dirtyCount} step{dirtyCount === 1 ? '' : 's'} edited
                </span>
              )}
            </div>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-3 bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : steps.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">This campaign has no steps yet.</p>
              <p className="text-xs text-gray-400 mt-1">Add steps to the campaign template before enrolling candidates.</p>
            </div>
          ) : (
            steps.map((s, idx) => renderStepCard(s, idx))
          )}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-between bg-white">
          <div className="text-xs text-gray-500">
            Edits apply to this enrollment only. The campaign template is not changed.
          </div>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleContinue}
              disabled={loading || steps.length === 0}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Continue to Contact Picker
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignCustomizerModal;
